<script>
  /** Recent pipeline activity and this tenant's portal audit trail. Counts and
   *  metadata only — no guest or draft text. */
  import api from '$lib/api.js';

  let { tenant } = $props();

  let days = $state(7);
  let loading = $state(true);
  let error = $state(null);
  let activity = $state(null);
  let audit = $state([]);

  async function load() {
    loading = true;
    error = null;
    try {
      const [a, log] = await Promise.all([
        api.getTenantActivity(tenant.id, days),
        api.listAudit({ tenant_id: tenant.id, limit: 25 }),
      ]);
      activity = a;
      audit = log.items;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { tenant.id; days; load(); });

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
  function summarize(changes) {
    if (!changes) return '';
    return Object.entries(changes)
      .map(([k, v]) => `${k}: ${typeof v === 'object' && v !== null ? JSON.stringify(v) : v}`)
      .join(' · ');
  }
</script>

<section class="space-y-6">
  <div class="flex items-center justify-between">
    <h2 class="text-sm font-semibold">Activity</h2>
    <select class="p-input w-auto text-xs" bind:value={days} aria-label="Window">
      <option value={1}>Last 24 hours</option>
      <option value={7}>Last 7 days</option>
      <option value={30}>Last 30 days</option>
    </select>
  </div>

  {#if error}
    <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
  {:else if loading && !activity}
    <p class="p-card p-4 text-sm p-muted">Loading…</p>
  {:else if activity}
    <div class="grid gap-4 sm:grid-cols-2">
      <div class="p-card p-4">
        <p class="mb-2 text-xs font-semibold uppercase tracking-wide p-muted">Inbound events</p>
        {#each activity.webhooks_by_status as row}
          <p class="flex justify-between text-sm"><span>{row.status}</span><span>{row.count}</span></p>
        {:else}<p class="text-sm p-muted">None.</p>{/each}
      </div>
      <div class="p-card p-4">
        <p class="mb-2 text-xs font-semibold uppercase tracking-wide p-muted">Responses</p>
        {#each activity.responses_by_type as row}
          <p class="flex justify-between text-sm"><span>{row.response_type}</span><span>{row.count}</span></p>
        {:else}<p class="text-sm p-muted">None.</p>{/each}
      </div>
    </div>

    <div class="p-card overflow-x-auto">
      <p class="px-4 pt-3 text-xs font-semibold uppercase tracking-wide p-muted">Latest responses</p>
      <table class="w-full text-left text-sm">
        <tbody>
          {#each activity.recent_responses as r (r.id)}
            <tr style="border-top: 1px solid var(--p-border)">
              <td class="px-4 py-2 text-xs p-muted">{fmt(r.created_at)}</td>
              <td class="px-4 py-2">{r.response_type}</td>
              <td class="px-4 py-2 text-xs">{r.trigger_reason ?? ''} {r.urgency_level ?? ''}</td>
              <td class="px-4 py-2 text-xs">
                {r.human_reviewed ? (r.human_approved ? 'approved' : (r.reviewed_by ?? 'closed')) : 'pending'}
              </td>
            </tr>
          {:else}
            <tr><td class="px-4 py-4 text-sm p-muted">No responses yet.</td></tr>
          {/each}
        </tbody>
      </table>
    </div>

    <div class="p-card overflow-x-auto">
      <p class="px-4 pt-3 text-xs font-semibold uppercase tracking-wide p-muted">Admin changes</p>
      <table class="w-full text-left text-sm">
        <tbody>
          {#each audit as entry (entry.id)}
            <tr style="border-top: 1px solid var(--p-border)">
              <td class="whitespace-nowrap px-4 py-2 text-xs p-muted">{fmt(entry.created_at)}</td>
              <td class="px-4 py-2 text-xs">{entry.user_email ?? '—'}</td>
              <td class="px-4 py-2">{entry.action}</td>
              <td class="px-4 py-2 text-xs p-faint">{summarize(entry.changes)}</td>
            </tr>
          {:else}
            <tr><td class="px-4 py-4 text-sm p-muted">No admin changes recorded.</td></tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</section>
